import { useState, useEffect, useCallback, useRef } from 'react';
import { cacheInvalidationService, RefreshProgress } from '@/services/cacheInvalidationService';
import { cacheVersionService } from '@/services/cacheVersionService';


interface CacheValidationState {
  isValidating: boolean;
  isInvalidating: boolean;
  progress: RefreshProgress | null;
  error: string | null;
  lastValidation: number | null;
}

export function useContentCacheValidation() {
  const [state, setState] = useState<CacheValidationState>({
    isValidating: false,
    isInvalidating: false,
    progress: null,
    error: null,
    lastValidation: null,
  });

  const isValidatingRef = useRef(false);

  const validateCache = useCallback(async (forceCheck?: boolean) => {
    if (isValidatingRef.current) {
      console.log('🔄 [useContentCacheValidation] Validation already in progress, skipping...');
      return;
    }

    isValidatingRef.current = true;
    setState((prev) => ({ ...prev, isValidating: true, error: null }));

    try {
      console.log(`🔄 [useContentCacheValidation] Starting cache validation... (forceCheck: ${!!forceCheck})`);

      const result = await cacheInvalidationService.checkAndInvalidateIfNeeded(
        (progress) => {
          setState((prev) => ({ ...prev, progress }));
        },
        forceCheck
      );

      if (!result.success) {
        setState((prev) => ({
          ...prev,
          isValidating: false,
          error: result.error || 'Cache validation failed',
          lastValidation: Date.now(),
        }));
        return;
      }

      console.log('🔄 [useContentCacheValidation] ✅ Cache validation complete');

      setState((prev) => ({
        ...prev,
        isValidating: false,
        progress: null,
        lastValidation: Date.now(),
      }));
    } catch (error) {
      console.error('🔄 [useContentCacheValidation] Error during validation:', error);
      setState((prev) => ({
        ...prev,
        isValidating: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        lastValidation: Date.now(),
      }));
    } finally {
      isValidatingRef.current = false;
    }
  }, []);

  const forceRefresh = useCallback(async () => {
    console.log('🔄 [useContentCacheValidation] ========================================');
    console.log('🔄 [useContentCacheValidation] 🚀 FORCE REFRESH INITIATED');
    console.log('🔄 [useContentCacheValidation] ========================================');

    setState((prev) => ({ ...prev, isInvalidating: true, error: null }));

    try {
      const startTime = Date.now();
      console.log('🔄 [useContentCacheValidation] Calling cacheInvalidationService.forceClearAndRefresh()...');

      const result = await cacheInvalidationService.forceClearAndRefresh(
        (progress) => {
          console.log(`🔄 [useContentCacheValidation] Progress update: ${progress.message} (${progress.progress}/${progress.total})`);
          setState((prev) => ({ ...prev, progress }));
        }
      );

      const duration = Date.now() - startTime;

      if (!result.success) {
        console.log('🔄 [useContentCacheValidation] ========================================');
        console.log('🔄 [useContentCacheValidation] ❌ FORCE REFRESH FAILED');
        console.log(`🔄 [useContentCacheValidation] Error: ${result.error}`);
        console.log(`🔄 [useContentCacheValidation] Duration: ${duration}ms`);
        console.log('🔄 [useContentCacheValidation] ========================================');

        setState((prev) => ({
          ...prev,
          isInvalidating: false,
          error: result.error || 'Force refresh failed',
        }));
        return;
      }

      console.log('🔄 [useContentCacheValidation] ========================================');
      console.log('🔄 [useContentCacheValidation] ✅ FORCE REFRESH COMPLETE');
      console.log(`🔄 [useContentCacheValidation] Duration: ${duration}ms`);
      console.log(`🔄 [useContentCacheValidation] Caches cleared: ${result.cachesClearedCount}`);
      console.log(`🔄 [useContentCacheValidation] Data refreshed: ${result.dataRefreshed}`);
      console.log('🔄 [useContentCacheValidation] ========================================');

      setState((prev) => ({
        ...prev,
        isInvalidating: false,
        progress: null,
        lastValidation: Date.now(),
      }));
    } catch (error) {
      console.log('🔄 [useContentCacheValidation] ========================================');
      console.error('🔄 [useContentCacheValidation] ❌ EXCEPTION during force refresh:', error);
      console.log('🔄 [useContentCacheValidation] ========================================');

      setState((prev) => ({
        ...prev,
        isInvalidating: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, []);

  const clearCacheOnly = useCallback(async () => {
    try {
      console.log('🔄 [useContentCacheValidation] Clearing cache...');
      await cacheInvalidationService.forceClearAllCaches();
      console.log('🔄 [useContentCacheValidation] ✅ Cache cleared');
    } catch (error) {
      console.error('🔄 [useContentCacheValidation] Error clearing cache:', error);
    }
  }, []);

  return {
    isValidating: state.isValidating,
    isInvalidating: state.isInvalidating,
    isRefreshing: state.isValidating || state.isInvalidating,
    progress: state.progress,
    error: state.error,
    lastValidation: state.lastValidation,
    forceRefresh,
    clearCacheOnly,
    recheckCache: validateCache,
  };
}
